import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';

import {
  useArtistProfileWorkHistoryMutation,
  useArtistProfileWorkHistoryQuery,
  useArtistProfileWorkHistoryStatusMutation
} from '@/features/artist/portfolio';

export const useArtistWorkHistory = () => {
  const { id: profileId } = useParams({
    from: '/artist/$id/_onboard/onboard/$step'
  });
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const router = useRouter();
  const queryClient = useQueryClient();
  const workHistory = useArtistProfileWorkHistoryQuery({ profileId });
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });
  const sendWorkHistoryStatus = useArtistProfileWorkHistoryStatusMutation({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['ArtistProfileWorkHistory', profileId]
        });
      }
    }
  });
  const sendWorkHistory = useArtistProfileWorkHistoryMutation({
    options: {
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: ['ArtistProfileWorkHistory', profileId]
        });
        await sendWorkHistoryStatus.mutateAsync({
          params: {
            profileId,
            workHistory: {
              hasWorkingExperience: false
            }
          }
        });
      }
    }
  });

  const worksHistory = useArtistProfileWorkHistoryQuery({ profileId });

  const onDelete = async (ItemIndex: number) => {
    if (workHistory.data?.data) {
      const currentItem = workHistory.data?.data.filter(
        (_, index) => index !== ItemIndex
      );

      if (workHistory.data?.data.length > 1) {
        await sendWorkHistoryStatus.mutateAsync({
          params: {
            profileId,
            workHistory: {
              hasWorkingExperience: true
            }
          }
        });
        return sendWorkHistory.mutateAsync({
          params: {
            profileId,
            workHistory: {
              hasWorkingExperience: true,
              workHistory: currentItem
            }
          }
        });
      }

      await sendWorkHistory.mutateAsync({
        params: {
          profileId,
          workHistory: {
            hasWorkingExperience: false,
            workHistory: currentItem
          }
        }
      });

      await sendWorkHistoryStatus.mutateAsync({
        params: {
          profileId,
          workHistory: {
            hasWorkingExperience: false
          }
        }
      });
    }
  };

  const continueWithExperience = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    navigate({
      to: '/artist/$id/onboard/$step',
      params: { step: 'languages' }
    });
  };

  const continueWithoutExperience = async () => {
    await sendWorkHistory.mutateAsync(
      {
        params: {
          profileId,
          workHistory: {
            workHistory: [],
            hasWorkingExperience: false
          }
        }
      },
      {
        onSuccess: () => {
          if (search.from !== undefined) {
            router.history.back();
          }
          navigate({
            to: '/artist/$id/onboard/$step',
            params: { step: 'languages' }
          });
        }
      }
    );
    await sendWorkHistoryStatus.mutateAsync({
      params: {
        profileId,
        workHistory: {
          hasWorkingExperience: false
        }
      }
    });
  };

  const queries = [workHistory, worksHistory];
  const isLoading = queries.some((q) => q.isLoading);
  return {
    state: { isLoading, worksHistory },
    functions: {
      continueWithExperience,
      continueWithoutExperience,
      onDelete
    }
  };
};
